import { useEffect } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import { useNavigate } from "react-router"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"

import useT from "utils/hooks/useT"
import {
  limsResults,
  limsSampleQc,
  limsConsumables,
  limsReviews,
  sampleView,
  assets,
} from "state_management"

// QA Analysis Review (SPEC-012; reworked by SPEC-022 K1/K5) — the LIMS worklist of analyses handed
// to QA. Lists every analysis with ≥1 result the reviewer has signed off and sent to QA (state
// "sent-to-qa"), one row per analysis with its count of samples awaiting a QA decision. This is a
// PURE worklist now: "Open" deep-links to the analysis ON THE PLAN, where the QA decisions section
// lives inside "QC Results & Approvals" (ResultEntryPanel/QaDecisionSection) — the separate
// QaAnalysisReviewModal replica is gone. The navigation is the one the modal's "Open analysis on
// the plan" link used: the plan lives at route "/" so we navigate the router there AND dispatch
// limsReviews.openReviewAnalysis, which jumps to the task's week and opens its config focused on
// the QC section (visual.activeAnalysisTaskId + focusQc, honoured by ScheduleElement).
//
// SPEC-022 (K10): the full list of OPEN OOS investigations lives on the dedicated "Investigations"
// menu screen (Phase 1 is lab work). QA Analysis Review keeps only the subset QA must DECIDE on —
// investigations that have reached the "qa-review" state (limsOos.selectQaReviewInvestigations).
// SPEC-022 (K1): the two worklists render SIDE BY SIDE on wide screens (stacked on narrow).
export default function QaAnalysisReview() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const worklist = useSelector(limsResults.selectQaAnalysisWorklist, shallowEqual)

  useEffect(() => {
    // SPEC-021 (B3): QC capabilities ride on the member dump (loaded app-wide) — no fetch here.
    dispatch(limsResults.fetch())
    dispatch(limsSampleQc.fetch())
    dispatch(limsConsumables.fetch())
    dispatch(assets.fetch())
    dispatch(sampleView.fetch())
  }, [dispatch])

  // Deep-link one worklist row to its analysis on the plan (SPEC-022 K5): navigate to the
  // schedule, jump to the analysis task's week and open its config with the QC section focused.
  const openAnalysisOnPlan = (analysisId) => {
    navigate("/")
    dispatch(limsReviews.openReviewAnalysis(analysisId))
  }

  return (
    <div className="LimsQueue">
      <h1 className="LimsQueue-title">{t("limsQaAnalysisReview")}</h1>

      <h2 className="LimsQueue-subtitle">{t("limsQaReviewPending")}</h2>
      {worklist.length === 0 ? (
        <p className="LimsQueue-empty">{t("limsQaReviewEmpty")}</p>
      ) : (
        <Table striped hover responsive className="LimsQueue-table LimsQueue-table--narrow">
          <thead>
            <tr>
              <th>{t("limsQaReviewAnalysis")}</th>
              <th>{t("limsQaReviewPending")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {worklist.map((row) => (
              <tr key={row.analysisId || "none"}>
                <td>{row.analysisLabel || "—"}</td>
                <td>{row.pending}</td>
                <td className="LimsQueue-actions">
                  <div className="LimsQueue-actions-inner">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => openAnalysisOnPlan(row.analysisId)}
                      disabled={!row.analysisId}
                    >
                      {t("limsQaReviewOpen")}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  )
}
